import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
} from 'react-native';
import { ms, s, vs } from '../utils/responsive';

interface TaskItemProps {
  task: {
    id: string;
    title: string;
  };
  isCompleted: boolean;
  onPress: (taskId: string) => void;
  onCheckboxPress: (taskId: string) => void;
  buttonText?: string;
  buttonColor?: string;
  hideActionButton?: boolean;
  subscriptionTier?: string;
  isDisabled?: boolean;  // Whether the button is disabled (e.g., subscription expired)
}

export const TaskItem: React.FC<TaskItemProps> = ({
  task,
  isCompleted,
  onPress,
  onCheckboxPress,
  buttonText = 'Click To Complete',
  buttonColor = '#29BFC0',
  hideActionButton = false,
  subscriptionTier = 'free',
  isDisabled = false,
}) => {
  // Determine button appearance based on disabled state
  const effectiveButtonColor = isDisabled ? '#CCCCCC' : buttonColor;
  const effectiveButtonText = isDisabled ? 'Locked' : buttonText;

  const handlePress = () => {
    if (!isDisabled) {
      onPress(task.id);
    }
  };

  const handleCheckboxPress = () => {
    if (!isDisabled) {
      onCheckboxPress(task.id);
    }
  };

  return (
    <View
      style={styles.taskItem}
      accessible={true}
      accessibilityRole="none"
      accessibilityLabel={`Task: ${task.title}. ${isCompleted ? 'Completed' : 'Not completed'}`}
    >
      <TouchableOpacity
        style={styles.checkboxContainer}
        onPress={handleCheckboxPress}
        disabled={isDisabled}
        accessibilityRole="checkbox"
        accessibilityLabel={`Mark ${task.title} as ${isCompleted ? 'incomplete' : 'complete'}`}
        accessibilityState={{ checked: isCompleted, disabled: isDisabled }}
        accessibilityHint={isDisabled ? 'Task is locked' : `Double tap to ${isCompleted ? 'unmark' : 'mark'} as complete`}
      >
        <View style={[
          styles.checkbox,
          isCompleted && styles.checkboxSelected,
          isDisabled && styles.checkboxDisabled
        ]}>
          {isCompleted && (
            <Image
              source={require('../assets/icons/check.png')}
              style={[styles.checkIcon, isDisabled && styles.checkIconDisabled]}
              accessibilityElementsHidden={true}
            />
          )}
        </View>
      </TouchableOpacity>

      <TouchableOpacity
        style={styles.taskInfo}
        onPress={handlePress}
        disabled={isDisabled}
        accessibilityRole="button"
        accessibilityLabel={task.title}
        accessibilityHint={isDisabled ? 'Task is locked' : 'Double tap to view or edit task details'}
        accessibilityState={{ disabled: isDisabled }}
      >
        <Text style={[styles.taskTitle, isDisabled && styles.taskTitleDisabled]}>
          {task.title}
        </Text>
      </TouchableOpacity>

      {!hideActionButton && (
        <TouchableOpacity
          style={[styles.completeButton, { backgroundColor: effectiveButtonColor }]}
          onPress={handlePress}
          disabled={isDisabled}
          accessibilityRole="button"
          accessibilityLabel={`${effectiveButtonText} for ${task.title}`}
          accessibilityHint={isDisabled ? 'Task is locked. Upgrade to access.' : 'Double tap to take action on this task'}
          accessibilityState={{ disabled: isDisabled }}
        >
          <Text style={styles.completeButtonText}>{effectiveButtonText}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  taskItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(15),
    marginBottom: ms(15),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  checkboxContainer: {
    marginRight: ms(15),
  },
  checkbox: {
    width: s(24),
    height: s(24),
    borderRadius: ms(12),
    borderWidth: ms(2),
    borderColor: '#29BFC0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#29BFC0',
  },
  checkboxDisabled: {
    borderColor: '#CCCCCC',
    backgroundColor: '#F0F0F0',
  },
  checkIcon: {
    width: s(14),
    height: s(14),
    tintColor: '#FFFFFF',
  },
  checkIconDisabled: {
    tintColor: '#AAAAAA',
  },
  taskInfo: {
    flex: 1,
  },
  taskTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
    color: '#1C2C4F',
  },
  taskTitleDisabled: {
    color: '#999999',
  },
  completeButton: {
    paddingHorizontal: ms(12),
    paddingVertical: ms(8),
    borderRadius: ms(20),
    backgroundColor: '#29BFC0',
  },
  completeButtonText: {
    color: '#FFFFFF',
    fontSize: ms(12, 0.3),
    fontWeight: '500',
  },
});
